import { Injectable } from "@angular/core";
import { AuthService } from "../api/auth.service";
import { ASSESSMENT } from 'src/app/ui-teacher/data/types';
import moment, { Moment } from "moment";
import { RoutesService } from "../api/routes.service";
import { AccountType } from "../constants/account-types";
import { LangService } from "../core/lang.service";
import { isEmpty } from "lodash";
import { District, Pagination, School, PaginatedRows, ConfirmationCode } from "../bc-accounts/bc-accounts.service";

export interface CreateNewSessionConfig {
    isScheduled: boolean,
    scheduled_time: string | null,
    school_class_id: number,
    slug: ASSESSMENT,
}

export interface AssessmentComponent {
    id: number,
    code: string,
    component: string,
    type: string,
    grade: number,
    language: string,
    assessment: string,
    name: string,
}

export interface TestWindow {
    id: number,
    dateStart: Date,
    dateEnd: Date,
    title?: {
        en?: string,
        fr?: string,
    },
    is_active: boolean,
    durationInMin: number,
    numAttemptsAllowed: number,
    allowInvigilatorUnsubmit: boolean,
    allowInvigilatorTakeTest: boolean,
    tabular: TestWindowTabular,
    assessments?: AssessmentComponent[],
    allAssessments?: string[],
    allocRules?: any[],
}

export interface TestSessionInfo {
    subSessions?: {
        studentStates?: Record<number, {
            uid: number,
            question_index: number,
            [key: string]: any,
        }>
    },
    activeSubSessions: any[],
    completedSubSessions: any[],
    [key: string]: any,
}

export interface TestWindowTabularRow {
    assessmentId: number,
    assessmentCode: string,
    title: string,
    subtitle?: string,
    grade: number,
    language: string,
    password: string,
    validOn: Moment,
    testSessionId?: number,
    schoolClassId?: number,
    schoolClassGroupId?: number,
    testSessionInfo?: TestSessionInfo,
}

export type TestWindowTabular = TestWindowTabularRow[];

export interface NewAssessmentConfig {
    name: string,
    startDate: string,
    endDate: string,
    components: string[],
}

export type NewTestWindowConfig = NewAssessmentConfig;

export interface SrResult {
    partA: {
        score: number,
        weight: number,
    },
    partB: {
        score: number,
        weight: number,
    },
}

export enum PROFICIENCY_LEVEL {
    EMERGING = 'Emerging',
    DEVELOPING = 'Developing',
    PROFICIENT = 'Proficient',
    EXTENDING = 'Extending',
    ON_TRACK = 'On Track',
}

export interface ProficiencyLevelRow {
    form: string,
    levelCount: Record<PROFICIENCY_LEVEL, number>,
}

export interface ProficiencyLevelFrequencyRow {
    level: PROFICIENCY_LEVEL,
    frequency: number,
    percent: number,
    cumulative: number,
}

export interface CutScoreRow {
    assessmentCode: string,
    assessmentName: string,
    form: string,
    developingLow: number,
    proficientLow: number,
    extendingLow: number
}

export interface StudentIrtRow {
    uid: number,
    pen: string,
    slug: string,
    irt_score: number,
}

export interface StudentIrtRow2 {
    uid: number,
    first_name: string,
    last_name: string,
    pen: string,
    assessment_code: string,
    score: number,
    lang: string,
    grade: number,
}

export enum AssessmentType {
    FSA = 'fsa',
    GRAD = 'grad',
}

export interface IndividualStudentReportRow {
    assessmentYear: number,
    firstName: string,
    lastName: string,
    schoolName: string,
    studentPENNumber: number,
    studentGrade: number,
    literacyLevel: string,
    numeracyLevel: string,
    submissionDateTimeNumSR: Moment,
    submissionDateTimeLitSR: Moment,
    submissionDateTimeCR: Moment,
    assessment: string,
}

export interface ScoreEntryStudentRow {
    uid: number,
    pen: string,
    first_name: string,
    last_name: string,
    theme: number,
    theme_taqr_id: number,
    LI_1: string,
    LI_1_taqr_id: number,
    LI_2: string,
    LI_2_taqr_id: number,
    LI_3: string,
    LI_3_taqr_id: number,
    NU_1: string,
    NU_1_taqr_id: number,
    NU_2: string,
    NU_2_taqr_id: number,
    NU_3: string,
    NU_3_taqr_id: number,

    _is_saved?: boolean
}

export interface SubmissionProgress {
    uid: number,
    progress: number,
    test_attempt_id: number,
    attempt_key: string,
    created_on: Moment,
    confirmationCode?: ConfirmationCode,
    hasUnsubmissions?: boolean,
}

export const ALL_ASSESSMENTS: AssessmentComponent = {
    id: -1,
    code: '',
    component: '',
    type: '',
    grade: -1,
    language: '',
    assessment: '',
    name: 'All assessments',
};

export interface SpreadSheetScoreEntryRow {
    pen: string,
    school_code: number,
    theme: 1 | 2,
    LI_1: string,
    LI_2: string,
    LI_3: string,
    NU_1: string,
    NU_2: string,
    NU_3: string,
    first_name: string,
    last_name: string,
}

export enum ScoreProfileType {
    THEME = 'Theme',
    SCORE = 'LI/NM',
}


export interface IScoreOption {
    slug: string,
    value: number,
    order: number,
}

export interface IScoreProfile {
    type: ScoreProfileType,
    options: IScoreOption[],
}

export type IThemeProfile = IScoreProfile;


@Injectable({
    providedIn: 'root'
})
export class BcAssessmentsService {
    constructor(private auth: AuthService, private routes: RoutesService, private lang: LangService) { }

    async getTestWindow(id: number, assessment: AssessmentType, withTestSession: boolean = true, withAssessments: boolean = true, withTabular: boolean = false): Promise<TestWindow | null> {
        const testWindow = await this.auth.apiGet(this.routes.MINISTRY_ADMIN_TEST_WINDOW, id, {
            query: {
                assessment,
                withTestSession: withTestSession ? 1 : 0,
                withAssessments: withAssessments ? 1 : 0,
                withTabular: withTabular ? 1 : 0,
            }
        });
        if (isEmpty(testWindow)) return null;
        return this.makeTestWindowFromData(testWindow);
    }

    async createNewTestWindow(config: NewTestWindowConfig, assessment: AssessmentType): Promise<void> {
        await this.auth.apiCreate(this.routes.MINISTRY_ADMIN_TEST_WINDOW, config, {
            query: {
                assessment,
            }
        });
    }

    async modifyTestWindowDates(id: number, newStartDate: string, newEndDate: string): Promise<void> {
        let assessmentConfig: NewTestWindowConfig = this.getInitialNewAssessmentConfig();
        assessmentConfig.startDate = newStartDate;
        assessmentConfig.endDate = newEndDate;

        await this.auth.apiPatch(this.routes.MINISTRY_ADMIN_TEST_WINDOW, id, assessmentConfig);
    }

    private makeTestWindowFromData(data: any): TestWindow {
        const testWindow: TestWindow = {
            id: data.id,
            dateStart: new Date(data.date_start),
            dateEnd: new Date(data.date_end),
            title: data.title ? JSON.parse(data.title) : null,
            durationInMin: data.duration_m,
            numAttemptsAllowed: data.is_multi_attempt == 1 ? data.num_attempts || 0 : 1,
            allowInvigilatorUnsubmit: data.is_invig_unsubmit == 1,
            allowInvigilatorTakeTest: data.is_invig_taketest == 1,
            is_active: data.is_active,
            assessments: data.assessments ? data.assessments.map(a => ({
                ...a,
                code: a.assessment_code,
            })) : undefined,
            allAssessments: data.assessments ? data.assessments.map(a => a.assessment_code) : undefined,
            allocRules: data.alloc_rules ? data.alloc_rules : undefined,
            tabular: data.tabular.map((t: any) => {
                const titleComponents = t.name.split(',')
                const row: TestWindowTabularRow = {
                    assessmentId: t.id,
                    assessmentCode: t.assessment_code,
                    title: titleComponents[0],
                    grade: t.grade,
                    subtitle: titleComponents.length > 1 ? titleComponents[1] : undefined,
                    language: t.language,
                    password: t.password,
                    validOn: moment.utc(t.valid_on),
                    testSessionId: t.test_session_id,
                    schoolClassId: t.school_class_id,
                    schoolClassGroupId: t.school_class_group_id,
                    testSessionInfo: t.sessionInfo,
                }
                return row;
            }),
        };
        return testWindow;
    }

    async findPastTestWindows(assessment: AssessmentType): Promise<TestWindow[]> {
        const data = <any[]>await this.auth.apiFind(this.routes.MINISTRY_ADMIN_TEST_WINDOW, {
            query: {
                assessment,
                isPast: 1,
                withTestSession: 1,
            }
        });
        return data.map(d => this.makeTestWindowFromData(d));
    }

    async findUpcomingTestWindows(assessment: AssessmentType): Promise<TestWindow[]> {
        const data = <any[]>await this.auth.apiFind(this.routes.MINISTRY_ADMIN_TEST_WINDOW, {
            query: {
                assessment,
                isUpcoming: 1,
                withTestSession: 1,
            }
        });
        return data.map(d => this.makeTestWindowFromData(d));
    }

    async findActiveTestWindow(accountType: AccountType, assessment: AssessmentType, withTestSession: boolean = true, withTabular: boolean = false, schoolGroupId?: number): Promise<TestWindow | null> {
        accountType === AccountType.MINISTRY_ADMIN ? this.routes.MINISTRY_ADMIN_TEST_WINDOW : this.routes.SCHOOL_ADMIN_TEST_WINDOW;
        let route: string;
        let extraQuery = {};
        if (accountType === AccountType.MINISTRY_ADMIN) {
            route = this.routes.MINISTRY_ADMIN_TEST_WINDOW;
        } else {
            route = this.routes.SCHOOL_ADMIN_TEST_WINDOW;
            extraQuery = {
                schl_group_id: schoolGroupId,
            };
        }

        const data = <any[]>await this.auth.apiFind(route, {
            query: {
                assessment,
                isActive: 1,
                withTestSession: withTestSession ? 1 : 0,
                withTabular: withTabular ? 1 : 0,
                withTDAllocRules: 1,
                withAssessments: 1,
                ...extraQuery,
            }
        });
        if (data.length === 0) return null;
        return this.makeTestWindowFromData(data[0]);
    }

    async findTestWindowsInRange(assessment: AssessmentType, range: { startDate: Moment, endDate: Moment }, withTestSession: boolean = true, withTabular: boolean = false): Promise<TestWindow[]> {
        const data = <any[]>await this.auth.apiFind(this.routes.MINISTRY_ADMIN_TEST_WINDOW, {
            query: {
                assessment,
                range: {
                    startDate: range.startDate.utc().toISOString(),
                    endDate: range.endDate.utc().toISOString(),
                },
                withTestSession: withTestSession ? 1 : 0,
                withTabular: withTabular ? 1 : 0,
            }
        });
        return data.map(d => this.makeTestWindowFromData(d));
    }

    async findTestWindowsByName(assessment: AssessmentType, name: string): Promise<TestWindow[]> {
        const data = <any[]>await this.auth.apiFind(this.routes.MINISTRY_ADMIN_TEST_WINDOW, {
            query: {
                assessment,
                name,
                withTestSession: 1,
            }
        });
        return data.map(d => this.makeTestWindowFromData(d));
    }

    async findAllTestWindows(assessment: AssessmentType, withTestSession: boolean = true, withTabular: boolean = false): Promise<TestWindow[]> {
        const data = <any[]>await this.auth.apiFind(this.routes.MINISTRY_ADMIN_TEST_WINDOW, {
            query: {
                assessment,
                withTestSession: withTestSession ? 1 : 0,
                withTabular: withTabular ? 1 : 0,
            },
        });
        return data.map(d => this.makeTestWindowFromData(d));
    }

    async getTestWindowById(id: number, assessment: AssessmentType, withTestSession: boolean = false, withTabular: boolean = false): Promise<TestWindow | null> {
        const data = <any>await this.auth.apiGet(this.routes.STUDENT_TEST_WINDOW, id, {
            query: {
                assessment,
                withTestSession: withTestSession ? 1 : 0,
                withTabular: withTabular ? 1 : 0,
            }
        });
        return this.makeTestWindowFromData(data);
    }

    async exportTestWindowSessionPasswords(testWindow: TestWindow, assessmentId: number) {
        let title = '-';
        if (testWindow.title && testWindow.title.en) {
            title += testWindow.title.en;
        }
        window.open(this.auth.reportFilePath(this.routes.MINISTRY_ADMIN_SESSION_PASSWORDS, JSON.stringify({
            query: {
                testWindowId: testWindow.id,
                assessmentId,
            }
        }), `Session-Passwords${title}-${testWindow.tabular.find(r => r.assessmentId === assessmentId).assessmentCode}`.split(' ').join('-')), '_blank');
    }

    async getSessionInfo(sessionId: number, classGroupId: number, classId: number, schoolGroupId?: number): Promise<TestSessionInfo> {
        const sessionInfo = await this.auth
            .apiGet(this.routes.SCHOOL_ADMIN_SESSION, sessionId, {
                query: {
                    school_class_group_id: classGroupId,
                    school_class_id: classId,
                    schl_group_id: schoolGroupId,
                },
            })

        return sessionInfo;
    }

    getInitialNewAssessmentConfig(): NewAssessmentConfig {
        return {
            name: '',
            startDate: this.toDateString(moment().toDate()),
            endDate: this.toDateString(moment().add({ month: 1 }).toDate()),
            components: [],
        }
    }

    private toDateString(date: Date): string {
        return (date.getFullYear().toString() + '-'
            + ("0" + (date.getMonth() + 1)).slice(-2) + '-'
            + ("0" + (date.getDate())).slice(-2))
            + 'T' + date.toTimeString().slice(0, 5);
    }

    getTestWindowTitle(testWindow: TestWindow): string {
        const fallbackName = 'Loading Assessment Session...';
        if (!testWindow || !testWindow.title) return fallbackName;
        if (this.lang.c() == 'en') {
            return testWindow.title.en || fallbackName;
        }
        if (this.lang.c() == 'fr') {
            return testWindow.title.fr || testWindow.title.en || fallbackName;
        }
        return fallbackName;
    }

    async getAllAssessmentComponents(assessment: 'grad' | 'fsa'): Promise<AssessmentComponent[]> {
        const components = <any[]>await this.auth.apiFind(this.routes.MINISTRY_ADMIN_ASSESSMENT_COMPONENTS, {
            query: {
                assessment,
            }
        });
        return components.map(c => ({
            ...c,
            code: c.assessment_code,
        }))
    }

    async getSrResultForTestAttempt(testAttemptId: number): Promise<SrResult> {
        const srResult = await this.auth.apiGet(this.routes.MINISTRY_ADMIN_BCGRAD_SR_RESULT, testAttemptId);
        return srResult;
    }

    isTestWindowUpcoming(testWindow: TestWindow): boolean {
        const dateStart = moment(testWindow.dateStart).utc();
        const now = moment().utc();
        return now.isBefore(dateStart);
    }

    async getStudentIrtScores(pagination: Pagination, testWindow: TestWindow, assessmentType: AssessmentType, district?: District, school?: School, assessment?: AssessmentComponent): Promise<{
        count: number,
        data: StudentIrtRow[],
    }> {
        const irtScores = await this.auth.apiFind(this.routes.MINISTRY_ADMIN_IRT_SCORE, {
            query: {
                pagination,
                testWindowId: testWindow.id,
                assessmentType: assessmentType,
                district: district ? district.groupId : undefined,
                school: school ? school.groupId : undefined,
                assessment: assessment ? assessment.code : undefined,
            },
        });
        return irtScores;
    }

    exportStudentIrtScores(assessmentType: AssessmentType, district?: District, school?: School, assessment?: AssessmentComponent) {
        let filename = 'Student-IRT-Scores-' + assessmentType;
        if (district) {
            filename += '-' + district.name;
        }
        if (school) {
            filename += '-' + school.name;
        }
        if (assessment) {
            filename += '-' + assessment.code;
        }
        const url = this.auth.reportFilePath(this.routes.MINISTRY_ADMIN_IRT_SCORE, JSON.stringify({
            query: {
                assessmentType: assessmentType,
                district: district ? district.groupId : undefined,
                school: school ? school.groupId : undefined,
                assessment: assessment ? assessment.code : undefined,
            }
        }), filename);
        window.open(url, '_blank');
    }

    async getIndividualStudentReportScores(pagination: Pagination, assessmentType: AssessmentType, testWindow: TestWindow, district?: District, school?: School, assessment?: AssessmentComponent, studentPEN?: number): Promise<{
        count: number,
        data: IndividualStudentReportRow[],
    }> {
        let irtScores = await this.auth.apiFind(this.routes.MINISTRY_ADMIN_IRT_SCORE, {
            query: {
                pagination,
                assessmentType: assessmentType,
                testWindowId: testWindow.id,
                isStudentResult: true,
                pen: studentPEN ? studentPEN : undefined,
                district: district ? district.groupId : undefined,
                school: school ? school.groupId : undefined,
                assessment: assessment ? assessment.code : undefined,
            },
        });

        const rows: Map<number, IndividualStudentReportRow> = new Map();
        irtScores.data.map(rawRow => {
            let existingRow = rows.get(rawRow.pen);
            if (existingRow) {
                if (rawRow.slug.startsWith('LI')) {
                    existingRow.literacyLevel = rawRow.prof_level;
                    if (rawRow.slug.includes('SR')) {
                        existingRow.submissionDateTimeLitSR = moment(rawRow.submission_dt);
                    }
                    else {
                        existingRow.submissionDateTimeCR = moment(rawRow.submission_dt);
                    }
                }
                else if (rawRow.slug.startsWith('NU')) {
                    existingRow.numeracyLevel = rawRow.prof_level;
                    if (rawRow.slug.includes('SR')) {
                        existingRow.submissionDateTimeNumSR = moment(rawRow.submission_dt);
                    }
                    else {
                        existingRow.submissionDateTimeCR = moment(rawRow.submission_dt);
                    }
                }
            } else {
                existingRow = {
                    assessmentYear: testWindow.dateEnd.getFullYear(),
                    firstName: rawRow.first_name,
                    lastName: rawRow.last_name,
                    schoolName: rawRow.school_name,
                    studentPENNumber: rawRow.pen,
                    studentGrade: rawRow.slug[3],
                    literacyLevel: undefined,
                    numeracyLevel: undefined,
                    assessment: rawRow.slug,
                    submissionDateTimeLitSR: undefined,
                    submissionDateTimeNumSR: undefined,
                    submissionDateTimeCR: undefined,
                };
                if (rawRow.slug.startsWith('LI')) {
                    existingRow.literacyLevel = rawRow.prof_level;
                    if (rawRow.slug.includes('SR')) {
                        existingRow.submissionDateTimeLitSR = moment(rawRow.submission_dt);
                    }
                    else {
                        existingRow.submissionDateTimeCR = moment(rawRow.submission_dt);
                    }
                }
                else if (rawRow.slug.startsWith('NU')) {
                    existingRow.numeracyLevel = rawRow.prof_level;
                    if (rawRow.slug.includes('SR')) {
                        existingRow.submissionDateTimeNumSR = moment(rawRow.submission_dt);
                    }
                    else {
                        existingRow.submissionDateTimeCR = moment(rawRow.submission_dt);
                    }
                }
                rows.set(rawRow.pen, existingRow);
            }
        });
        return {
            data: [...rows.values()].sort((a, b) => a.studentPENNumber < b.studentPENNumber ? -1 : 1),
            count: irtScores.count,
        }
    }

    async getStudentIrtScore__new(pagination: Pagination, assessment: AssessmentType, testWindow: TestWindow, group_by_student: boolean, district?: number, school?: number): Promise<PaginatedRows<StudentIrtRow2>> {
        return this.auth.apiFind(this.routes.MINISTRY_ADMIN_IRT_SCORE, {
            query: {
                new: 1,
                pagination,
                district_group_id: district,
                school_group_id: school,
                test_window_id: testWindow.id,
                assessment_type: assessment,
                group_by_student: group_by_student ? 1 : 0,
            }
        });
    }

    exportStudentIrtScore__new(assessment: AssessmentType, testWindow: TestWindow, district?: number, school?: number) {
        const url = this.auth.reportFilePath(this.routes.MINISTRY_ADMIN_IRT_SCORE, JSON.stringify({
            query: {
                new: 1,
                district_group_id: district,
                school_group_id: school,
                test_window_id: testWindow.id,
                assessment_type: assessment,
            }
        }), 'Student-IRT-Scores');
        window.open(url, '_blank');
    }

    async getStudentIrtScore__penOnly(pagination: Pagination, assessment: AssessmentType, testWindow: TestWindow, district?: number, school?: number): Promise<{
        pen: string,
        lang: string,
        grade: number,
    }[]> {
        return this.auth.apiFind(this.routes.MINISTRY_ADMIN_IRT_SCORE, {
            query: {
                new: 1,
                pagination,
                district_group_id: district,
                school_group_id: school,
                test_window_id: testWindow.id,
                assessment_type: assessment,
                pen_only: 1,
                group_by_student: 1,
            }
        });
    }

    async getValidationCodes(studentPen: number, testWindowId: number) {
        const codes = await this.auth.apiFind(this.routes.SCHOOL_ADMIN_BCG_STUDENTS_LOOKUP, {
            query: {
                action: 'find student codes',
                pen: studentPen,
                testWindowId,
            },
        });

        let validation = [];
        let logged = [];
        let special = [];

        for (const code of codes) {
            if (code.code_type == 'log') {
                logged.push(code);
            }
            else if (code.code_type == 'validation') {
                validation.push(code);
            }
            else if (code.code_type == 'special') {
                special.push(code);
            }
        }

        return { logged, validation, special };
    }

    async modifyFsaSessionPassword(assessmentCode: string, testWindow: TestWindow, password: string): Promise<void> {
        await this.auth.apiPatch(this.routes.MINISTRY_ADMIN_SESSION_PASSWORDS, 0, {
            password,
        }, {
            query: {
                assessmentCode,
                testWindowId: testWindow.id,
                assessmentType: AssessmentType.FSA,
            }
        });
    }

    async findStudentsForScoreEntry(pagination: Pagination, testWindow: TestWindow, districtGroupId: number, incompleteOnly: boolean = false, completeOnly: boolean = false, grade: -1 | 4 | 7, schoolGroupId?: number): Promise<PaginatedRows<ScoreEntryStudentRow>> {
        return await this.auth.apiFind(this.routes.DIST_ADMIN_SCORE_ENTRY, {
            query: {
                district_group_id: districtGroupId,
                school_group_id: schoolGroupId,
                pagination,
                test_window_id: testWindow.id,
                incomplete: incompleteOnly ? 1 : 0,
                complete: completeOnly ? 1 : 0,
                grade: grade == -1 ? undefined : grade,
            },
        });
    }

    async countStudentsCompletedSr(pagination: Pagination, testWindow: TestWindow, districtGroupId: number, grade: 4 | 7, schoolGroupId?: number): Promise<number> {
        const { count } = await this.auth.apiFind(this.routes.MINISTRY_ADMIN_ACCOUNTS, {
            query: {
                action: 'count',
                count: 'student count',
                sr_completed: 1,
                district: districtGroupId,
                school: schoolGroupId,
                pagination,
                grade,
                testWindowId: testWindow.id,
            }
        });
        return count;
    }

    async countStudentsForScoreEntry(pagination: Pagination, testWindow: TestWindow, districtGroupId: number, incompleteOnly: boolean = false, completeOnly: boolean = false, grade: -1 | 4 | 7, schoolGroupId?: number): Promise<number> {
        const { count } = await this.auth.apiFind(this.routes.DIST_ADMIN_SCORE_ENTRY, {
            query: {
                action: 'count',
                district_group_id: districtGroupId,
                school_group_id: schoolGroupId,
                pagination,
                test_window_id: testWindow.id,
                incomplete: incompleteOnly ? 1 : 0,
                complete: completeOnly ? 1 : 0,
                grade: grade == -1 ? undefined : grade,
            },
        });
        return count as number;
    }

    async getStudentForScoreEntry(studentUid: number, testWindow: TestWindow): Promise<ScoreEntryStudentRow[]> {
        return await this.auth.apiGet(this.routes.DIST_ADMIN_SCORE_ENTRY, studentUid, {
            query: {
                test_window_id: testWindow.id,
            },
        });
    }

    async changeScoreEntryThemeByUid(uid: number, schoolGroupId: number, testWindow: TestWindow, theme: any) {
        await this.auth.apiPatch(this.routes.DIST_ADMIN_SCORE_ENTRY, uid, {
            theme,
        }, {
            query: {
                school_group_id: schoolGroupId,
                test_window_id: testWindow.id,
            },
        });
    }

    async changeScoreEntryScoreByUid(uid: number, schoolGroupId: number, testWindow: TestWindow, slug: 'LI_1' | 'LI_2' | 'NU_1' | 'NU_2', score: string | number) {
        const component = ['LI_1', 'LI_2', 'LI_3'].includes(slug) ? 'literacy' : 'numeracy';
        const question = ['LI_1', 'NU_1'].includes(slug) ? 1 : (['LI_2', 'NU_2'].includes(slug) ? 2 : 3);
        if (score == "") {
            // delete score
            // await this.auth.apiRemove(this.routes.DIST_ADMIN_SCORE_ENTRY, {
            // score,
            // }, {
            // query: {
            // uid: uid,
            // school_group_id: schoolGroupId,
            // test_window_id: testWindow.id,
            // component: component,
            // question: question,
            // },
            // });
        }
        else {
            await this.auth.apiCreate(this.routes.DIST_ADMIN_SCORE_ENTRY, {
                score,
            }, {
                query: {
                    uid: uid,
                    school_group_id: schoolGroupId,
                    test_window_id: testWindow.id,
                    component: component,
                    question: question,
                },
            });
        }
    }

    async getStudentLogForScoreEntry(studentUid: number, taqr_ids: number[]): Promise<ScoreEntryStudentRow[]> {
        return await this.auth.apiFind(this.routes.MINISTRY_ADMIN_SCORE_ENTRY_LOGS, {
            query: {
                taqr_ids: taqr_ids,
            },
        });
    }

    async getStudentSubmissionProgress(uid: number, schoolGroupId: number, testWindow: TestWindow, assessmentSlug): Promise<SubmissionProgress | null> {
        const { progress } = await this.auth.apiGet(this.routes.MINISTRY_ADMIN_SUBMISSION_PROGRESS, uid, {
            query: {
                school_group_id: schoolGroupId,
                test_window_id: testWindow.id,
                assessment_slug: assessmentSlug,
            },
        });
        if (progress) {
            return {
                ...progress,
                created_on: moment(progress.created_on),
            };
        }
        return null
    }

    async unsubmitStudentAttempt(uid: number, testWindow: TestWindow, slug: string) {
        return await this.auth.apiPatch(this.routes.MINISTRY_ADMIN_UNSUBMISSIONS, uid, {}, {
            query: {
                test_window_id: testWindow.id,
                test_session_slug: slug,
            }
        });
    }

    async resetUnsubmission(uid: number, testWindow: TestWindow, slug: string) {
        return await this.auth.apiPatch(this.routes.MINISTRY_ADMIN_UNSUBMISSIONS, uid, {}, {
            query: {
                test_window_id: testWindow.id,
                test_session_slug: slug,
                reset: 1,
            }
        });
    }

    async isIrtReadyForTestWindow(testWindowId: number): Promise<boolean> {
        let data = await this.auth.apiGet(this.routes.MINISTRY_ADMIN_IRT_READY, testWindowId);
        return data.irt_ready == 1 ? true : false;
    }

    async uploadScoreEntry(test_window_id: number, rows: SpreadSheetScoreEntryRow[]): Promise<any> {
        return await this.auth.apiCreate(this.routes.DIST_ADMIN_SCORE_ENTRY, {
            upload: rows,
        }, {
            query: {
                upload: 1,
                test_window_id,
            },
        });
    }

    async getScoreProfile(type: ScoreProfileType): Promise<IScoreProfile> {
        return this.auth.apiGet(this.routes.SCHOOL_ADMIN_SCORE_PROFILE, type);
    }

}
